import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import UploadFileModal from '@nxsys/components/common/UploadFileModal';
import { useImportProspect } from '@nxsys/hooks/api/import';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { importAction } from '@nxsys/store/slices/import/reducer/importSlice';
import { GetImportProspectListParams } from '@nxsys/store/slices/import/types';
import { debounce } from 'lodash';
import {
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ImportResultUploadModal from '../components/ImportResultUploadModal';
import ViewImportedFile from '../components/ViewImportedFile';
import { IMPORT_SCREEN_TYPE } from '../constants';
import { getImportProspectColumns } from './columns';
import { IRELAND } from '@nxsys/store/constants/keys';

const ImportProspectTab = ({
  importFileRef,
}: {
  importFileRef: Ref<{ open: () => void }>;
}) => {
  const { messages } = useTypedIntl();
  const { isBureau, payrollCountry } = useSelector((state: AppState) => state.appStore);
  const isIreland = payrollCountry === IRELAND;

  const { importProspectList } = useSelector(
    (state: AppState) => state.importStore,
  );
  const { loading, list, pagination, queryParams } = importProspectList;

  const dispatch = useDispatch<AppDispatch>();

  const [searchValue, setSearchValue] = useState('');
  const [openUploadFileModal, setOpenUploadFileModal] = useState(false);
  const [openResultUploadModal, setOpenResultUploadModal] = useState(false);
  const [dataImported, setDataImported] = useState<IImportProspect | null>(
    null,
  );
  const [openViewImportedFileModal, setOpenViewImportedFileModal] =
    useState(false);

  useImperativeHandle(importFileRef, () => ({
    open: () => {
      setOpenUploadFileModal(true);
    },
  }));

  const { run: importProspect, loading: importProspectLoading } =
    useImportProspect();

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
    });
  }, []);

  const handleFetchData = (params: GetImportProspectListParams) => {
    dispatch(importAction.getImportProspectList(params));
  };

  const refetchData = () =>
    handleFetchData({
      ...queryParams,
    });

  const debouncedFetchData = useCallback(
    debounce((search: string) => {
      handleFetchData({
        ...queryParams,
        page: 0,
        size: 20,
        search: search,
      });
    }, 500),
    [queryParams],
  );

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    debouncedFetchData(value);
  };

  const handleUploaded = useCallback(
    async (fileData: { url: string; fileName: string; fileSize: number }) => {
      const params = {
        ...fileData,
        isIreland,
        isBureau
      };
      setOpenResultUploadModal(true);
      const res = await importProspect(params);
      setDataImported(res.data);
    },
    [],
  );

  const handleViewImportedFile = useCallback((data: IImportProspect) => {
    setDataImported(data);
    setOpenViewImportedFileModal(true);
  }, []);

  return (
    <>
      <div className="ImportProspectTab">
        <AppsHeader>
          <AppInputSearch value={searchValue} onChange={handleSearchChange} />
        </AppsHeader>
        <AppsContent>
          <AppTableContainer
            data={
              list?.map((item, index) => ({
                ...item,
                indexNumber:
                  pagination.current * pagination.pageSize + index + 1,
              })) ?? []
            }
            loading={loading}
            page={pagination.current}
            total={pagination.totalDocs}
            pageSize={pagination.pageSize}
            scroll={{ x: 1000, y: 700 }}
            columns={getImportProspectColumns({
              messages,
              onView: handleViewImportedFile,
            })}
            onChangePagination={onChangePagination}
            noDataType={!!queryParams?.search ? 'noDataFound' : 'noDataCreated'}
            addNewBtnName={messages['common.importFile']}
            addNewBtnOnClick={() => setOpenUploadFileModal(true)}
            addNewIcon={null}
          />
        </AppsContent>
      </div>
      <UploadFileModal
        open={openUploadFileModal}
        onClose={() => setOpenUploadFileModal(false)}
        onUploaded={handleUploaded}
        fileType="spreadsheet"
      />
      <ImportResultUploadModal
        isOpen={openResultUploadModal}
        toggle={() => setOpenResultUploadModal(!openResultUploadModal)}
        resultData={dataImported as any}
        loading={importProspectLoading}
        refetchData={refetchData}
        importScreenType={IMPORT_SCREEN_TYPE.PROSPECT}
      />
      <ViewImportedFile
        isOpen={openViewImportedFileModal}
        toggle={() => setOpenViewImportedFileModal(!openViewImportedFileModal)}
        resultData={dataImported}
        importScreenType={IMPORT_SCREEN_TYPE.PROSPECT}
      />
    </>
  );
};

export default ImportProspectTab;
